import { router, useLocalSearchParams, Redirect } from 'expo-router';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { BackChevron } from '@/components/BackChevron';
import { Button } from '@/components/Button';
import { Heading } from '@/components/Heading';
import { Overline } from '@/components/Overline';
import { Screen } from '@/components/Screen';
import { ClaimProductCard } from '@/components/gifting/ClaimProductCard';
import { GiftFallbackScreen } from '@/components/gifting/GiftFallbackScreen';
import { ShippingAddressForm } from '@/components/gifting/ShippingAddressForm';
import { Text } from '@/components/Text';
import { colors, radii, spacing } from '@/constants/theme';
import { GIFTING_ROUTES, giftingRouteFor } from '@/lib/giftingRoute';
import { API_ERROR_CODE, ApiError } from '@/services/errors';
import { authStore } from '@/stores/auth.store';
import { giftingStore } from '@/stores/gifting.store';

const ZIP_RE = /^\d{5}(-\d{4})?$/;
const STATE_RE = /^[A-Za-z]{2}$/;

/** Shipping + confirm. Draft in giftingStore; US-only. Races handled inline. */
const ConfirmGiftScreen = observer(() => {
  const { productId } = useLocalSearchParams<{ productId?: string }>();
  const product = giftingStore.productById(Number(productId));
  const { shippingDraft, claiming } = giftingStore;
  const [error, setError] = useState<string | null>(null);
  const [soldOutRace, setSoldOutRace] = useState(false);

  useEffect(() => {
    void giftingStore.ensureLoaded();
  }, []);

  useEffect(() => {
    const user = authStore.user;
    if (!giftingStore.shippingDraft.full_name && user) {
      const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
      if (name) giftingStore.updateShippingDraft({ full_name: name });
    }
  }, []);

  // Guards AFTER all hooks; a logged-out link must not collect an address.
  if (authStore.hydrated && !authStore.user) {
    return <Redirect href={GIFTING_ROUTES.landing} />;
  }

  // Render only while the server's step is 'choose' (adversarial finding).
  const step = giftingRouteFor(authStore.user);
  if (authStore.hydrated && step !== GIFTING_ROUTES.choose) {
    return <Redirect href={step ?? GIFTING_ROUTES.landing} />;
  }

  if (!product) {
    return <GiftFallbackScreen />;
  }

  const d = shippingDraft;
  const complete =
    d.full_name.trim().length > 0 &&
    d.street.trim().length > 0 &&
    d.city.trim().length > 0 &&
    STATE_RE.test(d.state.trim()) &&
    ZIP_RE.test(d.zip.trim());

  async function submit() {
    // Read `claiming` LIVE off the store, not the render-time destructure: a
    // double-tap in the same frame would see the stale false, and the
    // store's in-flight guard resolves silently — which this success path
    // would misread as a completed claim (review finding).
    if (!product || giftingStore.claiming) return;
    setError(null);
    setSoldOutRace(false);
    try {
      await giftingStore.claim(product.id);
      router.replace({ pathname: '/season-of-gifting/secured', params: { fresh: '1' } });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === API_ERROR_CODE.Unauthenticated) {
          // Session expired mid-form. The draft lives in the store, so it
          // survives the re-login round trip.
          router.replace({
            pathname: '/(auth)/login',
            params: { redirect: '/season-of-gifting/choose' },
          });
          return;
        }
        if (err.code === API_ERROR_CODE.GiftAlreadyClaimed) {
          // Another device won the race; refresh the stale payload first.
          void authStore.refreshMe().catch(() => undefined);
          router.replace('/season-of-gifting/secured');
          return;
        }
        if (err.code === API_ERROR_CODE.GiftSoldOut) {
          setSoldOutRace(true);
          void giftingStore.refreshAfterSoldOut();
          return;
        }
        if (err.code === API_ERROR_CODE.GiftingDisabled) {
          // Campaign closed mid-funnel. Refresh so the render guard reroutes
          // — error text alone leaves them retrying a locked door (Codex).
          setError("This campaign isn't available for this account yet.");
          void authStore.refreshMe().catch(() => undefined);
          return;
        }
        setError(err.message || 'Something went wrong. Please try again.');
        return;
      }
      setError('Something went wrong. Please try again.');
    }
  }

  return (
    <Screen keyboardAvoiding width="column">
      <BackChevron />
      <View style={styles.body}>
        <Heading size="4xl">
          Confirm your gift
        </Heading>

        <ClaimProductCard product={product} />

        {soldOutRace && (
          <View style={styles.raceBanner}>
            <Text variant="bodyMedium">That gift just sold out 😞</Text>
            <Text variant="caption" color="textMuted" style={styles.raceCopy}>
              Your address is saved — pick another gift and you won't retype it.
            </Text>
            <Button
              label="Pick another gift"
              variant="outline"
              size="sm"
              onPress={() => router.replace('/season-of-gifting/choose')}
              style={styles.raceCta}
            />
          </View>
        )}

        <Overline style={styles.shipLabel}>Shipping to (US only)</Overline>
        <ShippingAddressForm />

        {error && (
          <Text variant="caption" color="danger" style={styles.error}>
            {error}
          </Text>
        )}

        <Button
          label={claiming ? 'Claiming…' : 'Claim my gift'}
          onPress={() => void submit()}
          disabled={!complete || claiming || product.sold_out}
          style={styles.cta}
        />
        <Text variant="caption" color="textMuted" align="center" style={styles.disclosure}>
          Your name and address are shared with {product.brand_name} to ship your gift.
        </Text>
      </View>
    </Screen>
  );
});

export default ConfirmGiftScreen;

const styles = StyleSheet.create({
  body: {
    paddingTop: spacing.sm,
  },
  shipLabel: {
    marginTop: spacing.lg,
    marginBottom: spacing.md,
  },
  error: {
    marginTop: spacing.base,
  },
  cta: {
    marginTop: spacing.lg,
  },
  disclosure: {
    marginTop: spacing.md,
  },
  raceBanner: {
    backgroundColor: colors.warnPillBg,
    borderColor: colors.warnPillBorder,
    borderWidth: 1,
    borderRadius: radii.lg,
    padding: spacing.base,
    marginTop: spacing.lg,
  },
  raceCopy: {
    marginTop: spacing.xs,
  },
  raceCta: {
    marginTop: spacing.md,
    alignSelf: 'flex-start',
  },
});
